// src/pages/OrganiserProfilePage.js
// Public page for a single organiser — anyone can view it (no login
// needed to browse, matching the rest of Gather), but Follow itself
// requires signing in. Organiser's display name is derived from their
// own published events rather than the users collection, since a
// user's own profile doc isn't publicly readable — only their events are.
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../lib/AuthContext';
import { getOrganiserPublicEvents, getOrganiserFollowerCount, getMyFollowForOrganiser } from '../lib/db';
import { callFollowOrganiser } from '../lib/firebase';
import toast from 'react-hot-toast';
import BottomNav from '../components/layout/BottomNav';
import { PageBack } from '../components/shared/PageBack';

const fmtDate = (date) => {
  if (!date) return '';
  try {
    const d = date?.toDate ? date.toDate() : new Date(date);
    return format(d, 'EEE d MMM · h:mmaaa');
  } catch { return ''; }
};

const OrganiserProfilePage = () => {
  const { organiserId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [events, setEvents] = useState([]);
  const [followerCount, setFollowerCount] = useState(0);
  const [following, setFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [followBusy, setFollowBusy] = useState(false);

  const organiserName = events[0]?.organiserName || 'This organiser';

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [ev, count] = await Promise.all([
        getOrganiserPublicEvents(organiserId).catch(err => { console.error('getOrganiserPublicEvents failed:', err); return []; }),
        getOrganiserFollowerCount(organiserId).catch(err => { console.error('getOrganiserFollowerCount failed:', err); return 0; }),
      ]);
      setEvents(ev);
      setFollowerCount(count);
      if (user) {
        const isFollowing = await getMyFollowForOrganiser(organiserId, user.uid).catch(() => false);
        setFollowing(isFollowing);
      }
      setLoading(false);
    };
    load();
  }, [organiserId, user?.uid]);

  const handleFollowToggle = async () => {
    if (!user) { navigate('/login'); return; }
    setFollowBusy(true);
    try {
      await callFollowOrganiser({ organiserId, action: following ? 'unfollow' : 'follow' });
      setFollowing(!following);
      setFollowerCount(c => following ? Math.max(0, c - 1) : c + 1);
      toast.success(following ? 'Unfollowed' : `You're now following ${organiserName}`);
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong — please try again');
    }
    setFollowBusy(false);
  };

  if (loading) {
    return (
      <div style={{ background: 'var(--cream)', minHeight: '100vh', display: 'flex', justifyContent: 'center', paddingTop: 100 }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', paddingBottom: 100 }}>
      <div style={{ padding: '52px 20px 20px' }}>
        <PageBack fallbackPath="/" />

        <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--ink)', marginTop: 12, marginBottom: 4 }}>{organiserName}</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600, marginBottom: 16 }}>
          {followerCount} {followerCount === 1 ? 'follower' : 'followers'}
        </div>

        <button
          onClick={handleFollowToggle}
          disabled={followBusy}
          style={{
            width: '100%', border: 'none', borderRadius: 14, padding: 15, fontSize: 15, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer',
            background: following ? 'var(--sage-pale)' : 'var(--sage-deep)',
            color: following ? 'var(--sage-dark)' : 'white',
            opacity: followBusy ? 0.6 : 1,
          }}
        >
          {followBusy ? '...' : following ? '✓ Following' : '+ Follow'}
        </button>
      </div>

      <div style={{ padding: '0 20px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', marginBottom: 10 }}>
          Upcoming events {events.length > 0 ? `(${events.length})` : ''}
        </div>

        {events.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📅</div>
            <div className="empty-state-title">Nothing upcoming right now</div>
            <div className="empty-state-body">Follow to be notified once they post something new.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {events.map(event => (
              <button
                key={event.id}
                onClick={() => navigate(`/event/${event.id}`)}
                style={{ width: '100%', textAlign: 'left', background: 'var(--white)', border: 'none', borderRadius: 16, padding: '14px 16px', boxShadow: 'var(--shadow-sm)', cursor: 'pointer', display: 'block' }}
              >
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 4 }}>{event.title}</div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)', fontWeight: 600 }}>
                  {fmtDate(event.nextDate || event.date)}{event.venue ? ` · ${event.venue}` : ''}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default OrganiserProfilePage;
